/**
 * ============================================================================
 * DISCLAIMER MODAL - Project Status & Intent Disclosure
 * ============================================================================
 *
 * A modal dialog containing full project status disclosure with:
 * - Plain-language overview for non-technical readers
 * - Legal/technical details
 * - Voluntary support section
 * - Versioned disclaimer
 *
 * @author Ethereal Offering Team
 * @version 0.4 - January 2026
 */

import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Construction, Heart, ExternalLink, BookOpen, Scale } from 'lucide-react';

export default function DisclaimerModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('plain');
  const contentRef = useRef(null);

  // Reset to plain tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('plain');
      if (contentRef.current) contentRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // Notify global listeners about modal visibility changes
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('modalVisibilityChange', { detail: { isOpen: true } }));
    }
    return () => {
      if (isOpen) {
        window.dispatchEvent(new CustomEvent('modalVisibilityChange', { detail: { isOpen: false } }));
      }
    };
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sectionStyle = {
    marginBottom: '20px',
    padding: '16px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '12px',
    border: '1px solid rgba(156, 163, 175, 0.2)',
  };

  const headingStyle = {
    color: '#fbbf24',
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const textStyle = {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    margin: 0,
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Dark overlay backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              zIndex: 9999,
              backdropFilter: 'blur(4px)'
            }}
          />

          {/* Transparent clickable layer */}
          <div
            onClick={onClose}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10001,
              cursor: 'pointer'
            }}
          />

          {/* Modal content - Centered wrapper */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10002,
              pointerEvents: 'none'
            }}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="disclaimer-modal-title"
              aria-describedby="disclaimer-modal-description"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{
                width: '90vw',
                maxWidth: '600px',
                maxHeight: '85vh',
                background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95), rgba(31, 41, 55, 0.95))',
                border: '2px solid rgba(251, 191, 36, 0.4)',
                borderRadius: '24px',
                boxShadow: '0 20px 60px rgba(251, 191, 36, 0.2), 0 0 40px rgba(0,0,0,0.4)',
                backdropFilter: 'blur(12px)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                pointerEvents: 'auto'
              }}
            >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(156, 163, 175, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Construction size={24} color="#fbbf24" />
                <h2 id="disclaimer-modal-title" style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#ffffff',
                }}>
                  About This Project
                </h2>
              </div>
              <button
                onClick={onClose}
                aria-label="Close project status dialog"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={20} color="rgba(255,255,255,0.7)" />
              </button>
            </div>

            {/* Tab Buttons - Center Aligned */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '12px 24px 0',
              gap: '8px',
            }}>
              <TabButton active={activeTab === 'plain'} onClick={() => setActiveTab('plain')} icon={<BookOpen size={14} />} label="Plain Language" />
              <TabButton active={activeTab === 'legal'} onClick={() => setActiveTab('legal')} icon={<Scale size={14} />} label="Legal Details" />
            </div>

            {/* Scrollable Content */}
            <div ref={contentRef} id="disclaimer-modal-description" style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px 24px',
            }}>
              {activeTab === 'plain' ? (
                <PlainLanguageContent sectionStyle={sectionStyle} headingStyle={headingStyle} textStyle={textStyle} />
              ) : (
                <LegalContent sectionStyle={sectionStyle} headingStyle={headingStyle} textStyle={textStyle} />
              )}

              {/* Support Section */}
              <SupportSection sectionStyle={sectionStyle} headingStyle={headingStyle} textStyle={textStyle} />

              {/* Version Footer */}
              <div style={{
                textAlign: 'center',
                padding: '16px 0 8px',
                borderTop: '1px solid rgba(156, 163, 175, 0.2)',
                marginTop: '8px',
              }}>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                  <strong>Disclaimer Version:</strong> v0.4 | <strong>Last Updated:</strong> January 2026
                </p>
                <p style={{ margin: '8px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                  Built as an offering — not an institution, promise, or authority.
                </p>

                {/* Centered Close Button */}
                <button
                  onClick={onClose}
                  style={{
                    marginTop: '20px',
                    padding: '12px 32px',
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(236, 72, 153, 0.3))',
                    border: '1px solid rgba(251, 191, 36, 0.5)',
                    borderRadius: '12px',
                    color: '#fbbf24',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 191, 36, 0.5), rgba(236, 72, 153, 0.5))';
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(251, 191, 36, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(236, 72, 153, 0.3))';
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Close 🍄
                </button>
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

function TabButton({ active, onClick, icon, label }) {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: active
          ? 'rgba(251, 191, 36, 0.25)'
          : isHovered
            ? 'rgba(124, 58, 237, 0.2)'
            : 'transparent',
        border: `1px solid ${active ? 'rgba(251, 191, 36, 0.5)' : isHovered ? 'rgba(124, 58, 237, 0.5)' : 'rgba(156, 163, 175, 0.2)'}`,
        borderRadius: '10px',
        padding: '10px 16px',
        color: active ? '#fbbf24' : isHovered ? '#a78bfa' : 'rgba(255,255,255,0.6)',
        fontSize: '0.85rem',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.25s ease',
        transform: isHovered && !active ? 'scale(1.05)' : 'scale(1)',
        boxShadow: isHovered
          ? '0 4px 15px rgba(124, 58, 237, 0.3)'
          : active
            ? '0 2px 10px rgba(251, 191, 36, 0.2)'
            : 'none',
      }}
    >
      {icon} {label}
    </button>
  );
}

function PlainLanguageContent({ sectionStyle, headingStyle, textStyle }) {
  return (
    <>
      <div style={sectionStyle}>
        <h3 style={headingStyle}>What you're seeing</h3>
        <p style={textStyle}>
          This website and its features are <strong>works in progress</strong>. They are <strong>ideas and demonstrations</strong>, not finished products or official programs.
        </p>
      </div>

      <div style={sectionStyle}>
        <h3 style={headingStyle}>What it is (and isn't)</h3>
        <p style={textStyle}>
          This project is <strong>not yet connected to or approved by any outside organizations, fellowships, foundations, or blockchain networks</strong> that may be mentioned. Those references are shared for inspiration and exploration only.
        </p>
        <p style={{ ...textStyle, marginTop: '12px' }}>
          Nothing here should be understood as a promise, a product launch, or a financial opportunity.
        </p>
      </div>

      <div style={sectionStyle}>
        <h3 style={headingStyle}>Why it exists</h3>
        <p style={textStyle}>
          This space exists to explore a question:
        </p>
        <p style={{ ...textStyle, marginTop: '12px', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
          What might it look like to bring modern technology — like privacy-focused blockchain tools and community decision-making — into alignment with spiritual intention, service, recovery-informed values, and shared stewardship?
        </p>
        <p style={{ ...textStyle, marginTop: '12px' }}>
          The work is experimental by nature. Ideas may change, evolve, or be set aside as learning unfolds.
        </p>
      </div>

      <div style={sectionStyle}>
        <h3 style={headingStyle}>Who is building it</h3>
        <p style={textStyle}>
          This project is currently being built and stewarded by a single developer who also serves as a minister and A Course in Miracles teacher. It reflects a personal offering of study, prayerful inquiry, and technical experimentation — not an institution or authority.
        </p>
      </div>
    </>
  );
}

function LegalContent({ sectionStyle, headingStyle, textStyle }) {
  return (
    <>
      <div style={sectionStyle}>
        <h3 style={headingStyle}>Status Disclosure</h3>
        <p style={textStyle}>
          This website, its documentation, and any connected decentralized applications are <strong>early-stage, non-production demonstration concepts</strong> currently under active development. They are provided solely for <strong>exploratory, educational, and conceptual purposes</strong>.
        </p>
        <p style={{ ...textStyle, marginTop: '12px' }}>
          At this time, the project is <strong>not affiliated with, endorsed by, sponsored by, or formally connected to any external organizations, blockchain foundations, protocols, recovery fellowships, spiritual institutions, or governing bodies</strong>, whether named or implied.
        </p>
      </div>

      <div style={sectionStyle}>
        <h3 style={headingStyle}>No Offer or Representation</h3>
        <p style={textStyle}>
          Nothing presented here constitutes a financial product, investment opportunity, solicitation, legal agreement, or promise of future functionality. Features, terminology, architecture, and governance concepts are <strong>provisional, incomplete, and subject to change</strong> or removal at any time.
        </p>
      </div>

      <div style={sectionStyle}>
        <h3 style={headingStyle}>Exploratory Intent</h3>
        <p style={textStyle}>
          These demos exist as a <strong>brainstorming and prototyping environment</strong> — an inquiry into how blockchain primitives (privacy, coordination, transparency, governance, and cryptographic trust) might be thoughtfully aligned with:
        </p>
        <ul style={{ ...textStyle, marginTop: '12px', paddingLeft: '20px' }}>
          <li>Spiritual intention and contemplative practice</li>
          <li>Recovery-informed ethics and safety</li>
          <li>Service-oriented community governance</li>
          <li>Non-extractive technological design</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h3 style={headingStyle}>Development & Stewardship</h3>
        <p style={textStyle}>
          The project is currently stewarded independently by a single developer, minister, and ACIM teacher, and reflects a personal synthesis of technical research, spiritual study, and community service. All content and implementations should be understood in that context.
        </p>
      </div>
    </>
  );
}

function SupportSection({ sectionStyle, headingStyle, textStyle }) {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div
      style={{
        ...sectionStyle,
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(168, 85, 247, 0.1))',
        border: '1px solid rgba(168, 85, 247, 0.3)',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isHovered ? '0 8px 30px rgba(168, 85, 247, 0.3)' : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <h3 style={{ ...headingStyle, color: '#a855f7', justifyContent: 'center' }}>
        <Heart size={18} /> Voluntary Support
      </h3>
      <p style={textStyle}>
        If you feel aligned with the intent of this work and wish to support its continued development,
        you may choose to contribute directly to its steward.
      </p>
      <p style={{ ...textStyle, marginTop: '12px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
        Contributions are <strong>voluntary</strong>, <strong>non-refundable</strong>, and offered as support
        for ongoing labor and service — <strong>not</strong> in exchange for tokens, ownership, governance rights, or expected returns.
      </p>
      <SupportButton />
    </div>
  );
}

function SupportButton() {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <a
      href="https://drasticstatic.github.io/resume/index.html"
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '20px',
        padding: '14px 28px',
        background: isHovered
          ? 'linear-gradient(135deg, #a855f7, #7c3aed)'
          : 'linear-gradient(135deg, rgba(168, 85, 247, 0.4), rgba(139, 92, 246, 0.4))',
        border: '2px solid rgba(168, 85, 247, 0.6)',
        borderRadius: '12px',
        color: '#ffffff',
        fontSize: '1rem',
        fontWeight: 600,
        textDecoration: 'none',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'scale(1.08) translateY(-2px)' : 'scale(1)',
        boxShadow: isHovered
          ? '0 8px 30px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.3)'
          : '0 4px 15px rgba(168, 85, 247, 0.3)',
        cursor: 'pointer',
      }}
    >
      ✨ Support the Builder <ExternalLink size={14} />
    </a>
  );
}

